'use client';

import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { haptic } from '@/lib/haptic';
import { cn } from '@/lib/utils';

/**
 * A single menu row. `tone` is purely visual.
 *
 *   - default: foreground text
 *   - danger:  red text (e.g. Delete)
 *   - muted:   dimmed text (e.g. disabled placeholders)
 *
 * `keepOpenOnClick` is the escape hatch for items that flip a state
 * inside the menu itself (e.g. a "Visibility ▾" submenu trigger).
 */
export type ContextMenuItem = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  shortcut?: string;
  tone?: 'default' | 'danger' | 'muted';
  disabled?: boolean;
  separatorAfter?: boolean;
  keepOpenOnClick?: boolean;
  onSelect: () => void;
};

type Anchor = { x: number; y: number };

type FileContextMenuProps = {
  /** Items to render. Caller filters per-row (e.g. hide Rename when read-only). */
  items: ContextMenuItem[];
  /** Pointer-coordinate anchor in viewport space. Null = closed. */
  anchor: Anchor | null;
  /** Called when the menu wants to close (outside-click, Escape, item select). */
  onClose: () => void;
  /** Optional aria-label for the rendered menu container. */
  ariaLabel?: string;
};

// Layout constants. `MENU_WIDTH` doubles as the `min-width` floor —
// thin menus look chintzy and column-jumpy without a baseline width.
// `VIEWPORT_MARGIN` is the gutter we keep between the menu and the
// viewport edge when flipping a near-edge open.
const MENU_WIDTH = 220;
const VIEWPORT_MARGIN = 8;

/**
 * Right-click + long-press context menu portaled to document.body.
 *
 * The host wires `useFileContextMenu()` against any container, then
 * renders `<FileContextMenu items={...} anchor={...} onClose={...} />`
 * once. Items are typically derived from the row the user pointed at,
 * which we resolve through a small `target` cache the hook exposes.
 */
export function FileContextMenu({
  items,
  anchor,
  onClose,
  ariaLabel = 'Context menu',
}: FileContextMenuProps) {
  // We hold the inner div in a plain ref (for outside-click hit
  // testing) but attach it via a callback ref so we can measure +
  // flip the position the moment the node first attaches. A previous
  // implementation used `useRef` + `useLayoutEffect`, which raced
  // with `PopoverPortal`'s own mount effect: on the very first open
  // the portal returned `null` for one render, so `containerRef.current`
  // was null when the layout effect ran. By the time the portal
  // mounted its subtree, the deps `[anchor, measured, items]` hadn't
  // changed and the effect never re-ran, leaving the menu stuck at
  // `visibility: hidden` forever. Callback refs sidestep this entirely
  // — React calls them synchronously after attach.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Anchor | null>(null);

  // Seed `pos` from the raw anchor whenever a new anchor comes in.
  // The callback ref below will replace this with the measured /
  // flipped coordinates as soon as the menu's DOM node attaches.
  useEffect(() => {
    if (!anchor) {
      setPos(null);
      return;
    }
    setPos({ x: anchor.x, y: anchor.y });
  }, [anchor]);

  // Callback ref: fires synchronously when the inner div mounts (and
  // on every subsequent render with the same node, so we re-measure
  // if the item set changes height). Reads the real bounding rect,
  // flips up/left when the menu would overflow the viewport, and
  // commits the corrected position in the same tick — no separate
  // `measured` flag, no `visibility: hidden` cloak, no second paint
  // needed because React batches the `setPos` with the initial paint.
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (!node || !anchor) return;
      const rect = node.getBoundingClientRect();
      const w = rect.width || MENU_WIDTH;
      const h = rect.height || 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const x =
        anchor.x + w + VIEWPORT_MARGIN > vw
          ? Math.max(VIEWPORT_MARGIN, vw - w - VIEWPORT_MARGIN)
          : anchor.x;
      const y =
        anchor.y + h + VIEWPORT_MARGIN > vh
          ? Math.max(VIEWPORT_MARGIN, anchor.y - h)
          : anchor.y;
      // Only patch when the corrected position actually differs from
      // the raw anchor, otherwise we'd schedule an extra render for
      // every right-click in the middle of the viewport.
      if (x !== anchor.x || y !== anchor.y) {
        setPos((prev) =>
          prev && prev.x === x && prev.y === y ? prev : { x, y },
        );
      }
    },
    [anchor],
  );

  // Outside-click + Escape close. Bound to document so they fire even
  // when the user clicks the workspace canvas behind the portal.
  useEffect(() => {
    if (!anchor) return;
    const onPointer = (e: PointerEvent) => {
      const node = containerRef.current;
      if (!node) return;
      if (e.target instanceof Node && node.contains(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // pointerdown rather than click — close BEFORE the underlying
    // surface receives the event (so right-clicking another row
    // immediately replaces the menu instead of flashing closed/open).
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  if (!anchor || !pos) return null;

  const style: CSSProperties = {
    position: 'fixed',
    left: pos.x,
    top: pos.y,
    zIndex: 200,
    minWidth: MENU_WIDTH,
    maxWidth: 280,
  };

  return (
    <PopoverPortal style={style} role="menu" ariaLabel={ariaLabel}>
      <div
        ref={measureRef}
        className={cn(
          'glass-panel rounded-xl py-1 text-sm shadow-2xl',
          'origin-top-left animate-[wsCtxMenuIn_120ms_cubic-bezier(0.2,0.9,0.2,1)_forwards]',
        )}
      >
        {items.map((it, i) => {
          if (it.tone === 'muted' && it.disabled && typeof it.label === 'string' && !it.icon) {
            return (
              <div
                key={it.id}
                className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground select-none"
              >
                {it.label}
              </div>
            );
          }
          return (
            <div key={it.id}>
              <button
                type="button"
                role="menuitem"
                disabled={it.disabled}
                onClick={() => {
                  if (it.disabled) return;
                  it.onSelect();
                  if (!it.keepOpenOnClick) onClose();
                }}
                className={cn(
                  'group flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors',
                  it.disabled
                    ? 'cursor-not-allowed text-muted-foreground/70'
                    : 'hover:bg-foreground/8 active:bg-foreground/12',
                  it.tone === 'danger' && !it.disabled && 'text-red-600 dark:text-red-400',
                )}
              >
                {it.icon ? (
                  <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center opacity-80">
                    {it.icon}
                  </span>
                ) : (
                  <span className="inline-block h-4 w-4 shrink-0" />
                )}
                <span className="min-w-0 flex-1 truncate">{it.label}</span>
                {it.shortcut && (
                  <span className="ml-2 shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {it.shortcut}
                  </span>
                )}
              </button>
              {it.separatorAfter && i < items.length - 1 && (
                <div className="my-1 h-px shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]" />
              )}
            </div>
          );
        })}
      </div>
    </PopoverPortal>
  );
}

// ---------------------------------------------------------------------------
// Hook + helpers
// ---------------------------------------------------------------------------

/**
 * Hook that pairs with `<FileContextMenu />` to detect:
 *   - desktop right-click (`contextmenu` event) — instant open
 *   - mobile long-press (~500ms pointerdown, cancel on >8px move)
 *
 * Returns:
 *   - `anchor`:  pointer-coord position to feed to FileContextMenu
 *   - `target`:  the data payload the user pointed at (set by callers
 *                via `bindTrigger(payload)` on each row)
 *   - `close`:   manual close (for after running an action)
 *   - `bindTrigger`: returns event handlers to spread on a row
 *
 * Generic over T so a tab can attach (e.g.) `{ kind: 'agent'; row: AgentRow }`
 * to each binding and discriminate inside its item set.
 */
export function useFileContextMenu<T>() {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [target, setTarget] = useState<T | null>(null);

  const longPressTimer = useRef<number | null>(null);
  const longPressStart = useRef<Anchor | null>(null);
  const LONG_PRESS_MS = 500;
  const MOVE_CANCEL_PX = 8;

  const clearLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressStart.current = null;
  };

  const close = useCallback(() => {
    setAnchor(null);
    setTarget(null);
    clearLongPress();
  }, []);

  const bindTrigger = useCallback(
    (payload: T) => ({
      onContextMenu: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setTarget(payload);
        setAnchor({ x: e.clientX, y: e.clientY });
      },
      onPointerDown: (e: React.PointerEvent) => {
        // Only treat touch/pen as long-press triggers. Mouse uses
        // the native `contextmenu` event above.
        if (e.pointerType === 'mouse') return;
        longPressStart.current = { x: e.clientX, y: e.clientY };
        longPressTimer.current = window.setTimeout(() => {
          haptic('medium');
          setTarget(payload);
          setAnchor({ x: e.clientX, y: e.clientY });
          longPressTimer.current = null;
        }, LONG_PRESS_MS);
      },
      onPointerMove: (e: React.PointerEvent) => {
        const start = longPressStart.current;
        if (!start) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) {
          clearLongPress();
        }
      },
      onPointerUp: () => {
        clearLongPress();
      },
      onPointerCancel: () => {
        clearLongPress();
      },
    }),
    [],
  );

  return { anchor, target, close, bindTrigger };
}
